import { Link } from 'react-router-dom';
import { Smartphone, Code, Shield, Globe, Monitor, ExternalLink, Layers } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import dietbuxImage from '../assets/dietbuxm.png';
import dietvalueImage from '../assets/dietvaluem.png';
import basicImage from '../assets/basicm.png';
import balancedBiteImage from '../assets/balancedbitem.png';
import dieterImage from '../assets/dieterm.png';
import planetZeroImage from '../assets/planetzerom.png';
import calImage from '../assets/calm.png';
import onethirdImage from '../assets/onethirdm.png';
import purehealthImage from '../assets/purehealthm.png';
import approvedlifeImage from '../assets/approvedlifem.png';
import trymacroImage from '../assets/macrom.png';
import calculateImage from '../assets/calculatem.png';
import { Helmet } from 'react-helmet';
import { getPageKeywords } from '../pages/hooks/keywordsService';
import { useTranslation } from "react-i18next";

const MobileApp = () => {
  const [keywords, setKeywords] = useState('mobile app development, iOS, Android, cross-platform apps, mobile solutions');
  const { t, i18n } = useTranslation();
  
  // Get current language direction
  const isRTL = i18n.language === 'ar';

  const canonicalUrl = "https://cocopalms.io/what-we-do/mobile-app";

  // Update meta description for mobile app page
  useEffect(() => {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Cocopalms creates high-performance mobile applications for F&B and enterprise, delivering exceptional user experiences across iOS and Android platforms.');
    }
  }, []);

  useEffect(() => {
    const fetchKeywords = async () => {
      const pageKeywords = await getPageKeywords('what-we-do/mobile-app');
      if (pageKeywords) {
        setKeywords(pageKeywords);
      }
    };
    fetchKeywords();
  }, []);

  // Add useEffect for canonical URL in document head (same pattern as AboutUs and WhatWeDo)
  useEffect(() => {
    // Remove any existing canonical links
    const existingCanonical = document.querySelector("link[rel='canonical']");
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Create and add new canonical link
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = canonicalUrl;
    document.head.appendChild(canonicalLink);

    // Add robots meta tag if missing
    if (!document.querySelector("meta[name='robots']")) {
      const robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      robotsMeta.content = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
      document.head.appendChild(robotsMeta);
    }

    // Cleanup function
    return () => {
      const canonical = document.querySelector("link[rel='canonical']");
      if (canonical && canonical.href === canonicalUrl) {
        canonical.remove();
      }
    };
  }, [canonicalUrl]);

  // Updated apps array with translation keys instead of hardcoded content
  const apps = [
    {
      id: 'dietbux',
      name: 'Dietbux',
      url: 'https://dietbux.com/',
      image: dietbuxImage,
      category: 'Health & Nutrition',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'dietvalue',
      name: 'Dietvalue',
      url: 'https://dietvaluekw.com/',
      image: dietvalueImage,
      category: 'Food Delivery',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'basic',
      name: 'Basic',
      url: 'https://basickw.com/',
      image: basicImage,
      category: 'Business',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'balancedBite',
      name: 'Balanced Bite',
      url: 'https://dietprokuwait.com/',
      image: balancedBiteImage,
      category: 'Health & Wellness',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'dieter',
      name: 'Dieter',
      url: 'https://dieterkwt.com/',
      image: dieterImage,
      category: 'Health & Nutrition',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'planetZero',
      name: 'Planet Zero',
      url: 'https://play.google.com/store/apps/details?id=io.cocopalms.planetzero&hl=en',
      image: planetZeroImage,
      category: 'Food Delivery',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'cal',
      name: 'Cal',
      url: 'https://apps.apple.com/kw/app/cal-%D9%83%D8%A7%D9%84/id1667845619',
      image: calImage,
      category: 'Food Delivery',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'oneThird',
      name: 'OneThird',
      url: 'https://play.google.com/store/apps/details?id=io.cocopalms.onethird&hl=en',
      image: onethirdImage,
      category: 'Food Delivery',
      platforms: ['Android']
    },
    {
      id: 'pureHealth',
      name: 'PureHealth',
      url: 'https://apps.apple.com/qa/app/purehealth-kuwait/id6475193454?platform=iphone',
      image: purehealthImage,
      category: 'Food Delivery',
      platforms: ['iOS', 'Android']
    },
    {
      id: 'approvedLife',
      name: 'Approved Life',
      url: 'https://play.google.com/store/apps/details?id=io.cocopalms.approvedlife&hl=en',
      image: approvedlifeImage,
      category: 'Food Delivery',
      platforms: ['Android']
    },
    {
      id: 'tryMacro',
      name: 'Try Macro',
      url: 'https://apps.apple.com/kw/app/try-macro/id1671781444',
      image: trymacroImage,
      category: 'Food Delivery',
      platforms: ['iOS']
    },
    {
      id: 'calculate',
      name: 'Calculate',
      url: 'https://apps.apple.com/in/app/calculate-for-life/id1671781468',
      image: calculateImage,
      category: 'Food Delivery',
      platforms: ['iOS', 'Android']
    }
  ];

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>Mobile Application Platform | Cocopalms for F&B & Enterprise</title>
        <meta 
          name="description" 
          content="Cocopalms creates high-performance mobile applications for F&B and enterprise, delivering exceptional user experiences across iOS and Android platforms"
        />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://cocopalms.io/what-we-do/mobile-app" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Mobile App Development Services | Cocopalms - iOS & Android Apps" />
        <meta property="og:description" content="Professional mobile app development services in Kuwait. Native iOS & Android apps, cross-platform solutions, UI/UX design. View our portfolio of 12+ successful mobile applications." />
        <meta property="og:url" content="https://cocopalms.io/what-we-do/mobile-app" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cocopalms" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mobile App Development Services | Cocopalms - iOS & Android Apps" />
        <meta name="twitter:description" content="Professional mobile app development services in Kuwait. Native iOS & Android apps, cross-platform solutions, UI/UX design. View our portfolio of 12+ successful mobile applications." />
        
        {/* Additional SEO Meta Tags */}
        <meta name="author" content="Cocopalms" />
        
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Mobile App Development Services",
            "provider": {
              "@type": "Organization",
              "name": "Cocopalms",
              "url": "https://cocopalms.io"
            },
            "description": "Professional mobile app development services in Kuwait. Native iOS & Android apps, cross-platform solutions, UI/UX design.",
            "url": "https://cocopalms.io/what-we-do/mobile-app",
            "serviceType": "Mobile App Development",
            "areaServed": {
              "@type": "Country",
              "name": "Kuwait"
            },
            "offers": [
              {
                "@type": "Service",
                "name": "Native App Development",
                "description": "High-performance native apps for iOS and Android"
              },
              {
                "@type": "Service", 
                "name": "Cross-Platform Development",
                "description": "Cost-effective solutions using React Native and Flutter"
              },
              {
                "@type": "Service",
                "name": "Mobile UI/UX Design", 
                "description": "Intuitive and engaging user interfaces"
              }
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Mobile App Portfolio",
              "itemListElement": [
                {
                  "@type": "MobileApplication",
                  "name": "Dietbux",
                  "applicationCategory": "Health & Nutrition",
                  "operatingSystem": ["iOS", "Android"],
                  "url": "https://dietbux.com/"
                },
                {
                  "@type": "MobileApplication", 
                  "name": "Planet Zero",
                  "applicationCategory": "Food Delivery",
                  "operatingSystem": ["iOS", "Android"],
                  "url": "https://play.google.com/store/apps/details?id=io.cocopalms.planetzero"
                },
                {
                  "@type": "MobileApplication",
                  "name": "Cal",
                  "applicationCategory": "Food Delivery", 
                  "operatingSystem": ["iOS", "Android"],
                  "url": "https://apps.apple.com/kw/app/cal-%D9%83%D8%A7%D9%84/id1667845619"
                }
              ]
            }
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-custom-teal py-20 md:py-32 px-4 mt-24">
        <div className="container mx-auto text-center max-w-5xl">
          <div className="bg-white/15 backdrop-blur-sm w-20 h-20 rounded-2xl flex items-center justify-center mb-8 mx-auto">
            <Smartphone className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {t('mobileApp.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8">
            {t('mobileApp.hero.subtitle')}
          </p>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mx-auto">
            {t('mobileApp.hero.description')}
          </p>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('mobileApp.portfolio.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('mobileApp.portfolio.subtitle')}
            </p>
          </div>

          <div className="space-y-24">
            {apps.map((app, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? (isRTL ? 'lg:flex-row-reverse' : 'lg:flex-row') : (isRTL ? 'lg:flex-row' : 'lg:flex-row-reverse')} items-center gap-16 lg:gap-20`}>
                {/* Mobile Frame Image */}
                <div className="flex-1 flex justify-center">
                  <div className="relative group">
                    {/* Glow effect behind the phone */}
                    <div className="absolute -inset-8 bg-gradient-to-r from-custom-teal/20 to-teal-600/20 rounded-[3rem] blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                    
                    {/* Phone frame container */}
                    <div className="relative transform group-hover:scale-105 transition-all duration-500">
                      <img 
                        src={app.image} 
                        alt={`${app.name} Mobile App Interface`}
                        className="w-[700px] md:w-[900px] lg:w-[1100px] xl:w-[1300px] 2xl:w-[1500px] h-auto object-contain drop-shadow-2xl"
                      />
                    </div>
                  </div>
                </div>

                {/* App Details */}
                <div className="flex-1 space-y-6 max-w-xl">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="bg-custom-teal/10 text-custom-teal px-4 py-2 rounded-full text-sm font-medium">
                        {t(`mobileApp.portfolio.apps.${app.id}.category`)}
                      </span>
                      <div className="flex gap-2">
                        {app.platforms.map((platform, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                            {platform}
                          </span>
                        ))}
                      </div>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                      {t(`mobileApp.portfolio.apps.${app.id}.name`)}
                    </h3>
                    <p className="text-lg text-gray-600 leading-relaxed">
                      {t(`mobileApp.portfolio.apps.${app.id}.description`)}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-xl font-semibold text-gray-900">{t('mobileApp.portfolio.keyFeatures')}</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {t(`mobileApp.portfolio.apps.${app.id}.features`, { returnObjects: true }).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`w-2 h-2 bg-custom-teal rounded-full flex-shrink-0 ${isRTL ? 'ml-auto' : ''}`}></div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-6">
                    <a 
                      href={app.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-gradient-to-r from-custom-teal to-teal-600 text-white px-8 py-4 rounded-xl font-medium hover:from-teal-600 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      {t('mobileApp.portfolio.viewLiveApp')}
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl md:text-4xl font-bold text-custom-teal">12+</div>
              <div className="text-gray-600">{t('mobileApp.stats.appsDelivered')}</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl md:text-4xl font-bold text-teal-600">100%</div>
              <div className="text-gray-600">{t('mobileApp.stats.clientSatisfaction')}</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl md:text-4xl font-bold text-teal-700">2</div>
              <div className="text-gray-600">{t('mobileApp.stats.platforms')}</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl md:text-4xl font-bold text-custom-teal">24/7</div>
              <div className="text-gray-600">{t('mobileApp.stats.support')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('mobileApp.services.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('mobileApp.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-custom-teal/5 to-teal-50 hover:from-custom-teal/10 hover:to-teal-100 transition-all duration-300">
              <div className="bg-custom-teal w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.nativeApp.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.nativeApp.description')}
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 transition-all duration-300">
              <div className="bg-teal-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Code className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.crossPlatform.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.crossPlatform.description')}
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-all duration-300">
              <div className="bg-emerald-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Layers className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.uiUx.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.uiUx.description')}
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-cyan-50 to-teal-50 hover:from-cyan-100 hover:to-teal-100 transition-all duration-300">
              <div className="bg-cyan-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Globe className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.apiIntegration.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.apiIntegration.description')}
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-teal-50 to-blue-50 hover:from-teal-100 hover:to-blue-100 transition-all duration-300">
              <div className="bg-teal-700 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.appSecurity.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.appSecurity.description')}
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-teal-50 hover:from-slate-100 hover:to-teal-100 transition-all duration-300">
              <div className="bg-slate-600 w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Monitor className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('mobileApp.services.appStoreOptimization.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('mobileApp.services.appStoreOptimization.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('mobileApp.technologies.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('mobileApp.technologies.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'Node.js'].map((tech, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 group-hover:scale-105 border border-custom-teal/10 hover:border-custom-teal/20">
                  <h4 className="font-semibold text-gray-900 text-sm">{tech}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-custom-teal to-teal-600">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('mobileApp.cta.title')}
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t('mobileApp.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-medium transition duration-300"
            >
              {t('mobileApp.cta.startProject')}
            </Link>
            <Link 
              to="/what-we-do" 
              className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-3 rounded-lg font-medium transition duration-300"
            >
              {t('mobileApp.cta.exploreServices')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MobileApp;